// AuthContext.jsx
import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  signOut, 
  onAuthStateChanged,
  sendPasswordResetEmail
} from 'firebase/auth';
import { getFirebaseAuth, firebaseReady } from '../config/firebase';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscriptionPlan, setSubscriptionPlan] = useState(null);
  const [isSubscriptionLoading, setIsSubscriptionLoading] = useState(false);
  const [firebaseInitialized, setFirebaseInitialized] = useState(false);

  // Sign out
  const logout = async () => {
    try {
      const auth = await getFirebaseAuth();
      await signOut(auth);
      setSubscriptionPlan(null);
    } catch (error) {

      throw error;
    }
  };

  // Password reset
  const resetPassword = async (email) => {
    try {
      const auth = await getFirebaseAuth();
      await sendPasswordResetEmail(auth, email);
    } catch (error) {

      throw error;
    }
  };

  // Check subscription status from backend
  const checkSubscription = async (user) => {
    if (!user) {
      setSubscriptionPlan(null);
      setIsSubscriptionLoading(false);
      return;
    }

    setIsSubscriptionLoading(true);
    try {
      const token = await user.getIdToken();
      const response = await fetch(`/api/subscription/status`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setSubscriptionPlan(data.subscriptionPlan || null);
      } else if (response.status === 404) {
        // New user profile not created yet - this is expected during signup
        setSubscriptionPlan(null);
      } else {
        setSubscriptionPlan(null);
      }
    } catch (error) {
      // Suppress network errors during signup process
      if (!error.message.includes('404')) {

      }
      setSubscriptionPlan(null);
    } finally {
      setIsSubscriptionLoading(false);
    }
  };

  useEffect(() => {
    let unsubscribe = null;
    
    // Initialize Firebase and set up auth listener
    const initAuth = async () => {
      try {
        await firebaseReady;
        const auth = await getFirebaseAuth();
        setFirebaseInitialized(true);
        
        unsubscribe = onAuthStateChanged(auth, (user) => {
          setCurrentUser(user);
          setLoading(false);
          
          if (user) {
            checkSubscription(user);
          } else {
            setSubscriptionPlan(null);
          }
        });
      } catch (error) {

        setLoading(false);
      }
    };

    initAuth();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  const value = {
    currentUser,
    logout,
    resetPassword,
    subscriptionPlan,
    isSubscriptionLoading,
    checkSubscription,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}